import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
// FP3-035F Issue 2 — Preserve ?as_provider= when admin is shadowing.
import useShadowNavigate from "@/lib/useShadowNavigate";
// FP3-035F Issue 2 — Block Live Session entirely in read-only mode.
// FP3-043 Phase A follow-up #9 — Same gate applies to admin-family
// users entering /session directly (Admin → Students drill-in).
import { useStudentWorkspaceReadOnly } from "@/components/layout/ReadOnlyProviderBanner";
import { Lightning, Check, X, Hand, Tilde, Notepad, ArrowRight, Play, Stop, Target, User, ArrowLeft } from "@phosphor-icons/react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";
import api from "@/lib/api";
// FP3-125 — Live Session must bust admin/progress/reports caches so
// dashboards + Reports Attendance reflect the new evidence without
// a manual Refresh.
import { invalidateAfterMutation } from "@/lib/adminQueries";
import { DOCUMENT_SESSION_MINUTE_OPTIONS } from "@/lib/serviceDurations";

function formatTime(t) {
  if (!t) return "";
  const [h, m] = t.split(":");
  const hour = parseInt(h);
  return `${hour % 12 || 12}:${m} ${hour >= 12 ? "PM" : "AM"}`;
}

// FP3-128 — Human-readable ISO date for the Previous Session Note.
// Accepts "YYYY-MM-DD" and renders "Mon DD, YYYY" so the surface
// reads "Aug 17, 2026" per the acceptance example. Timezone-safe:
// we parse the components manually to avoid UTC-vs-local drift
// (a bare `new Date("2026-08-17")` renders as Aug 16 in negative
// UTC offsets — providers must see the calendar date the session
// occurred on).
function formatPrevNoteDate(iso) {
  if (!iso || typeof iso !== "string") return "";
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return iso;
  const months = [
    "Jan", "Feb", "Mar", "Apr", "May", "Jun",
    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
  ];
  const [, y, mo, d] = m;
  const monthIdx = parseInt(mo, 10) - 1;
  if (monthIdx < 0 || monthIdx > 11) return iso;
  return `${months[monthIdx]} ${parseInt(d, 10)}, ${y}`;
}

export default function LiveDataCollection() {
  const navigate = useShadowNavigate();
  const readOnly = useStudentWorkspaceReadOnly();
  const [searchParams] = useSearchParams();
  const isQuickMode = searchParams.get("quick") === "true";
  const launchParam = searchParams.get("launch");
  const [provider, setProvider] = useState(null);
  const [todaySessions, setTodaySessions] = useState([]);
  const [allStudents, setAllStudents] = useState([]);
  const [quickSelected, setQuickSelected] = useState([]);
  const [loading, setLoading] = useState(true);

  // Active session state
  const [activeSession, setActiveSession] = useState(null);
  const [sessionStudents, setSessionStudents] = useState([]);
  const [selectedStudentIdx, setSelectedStudentIdx] = useState(0);
  const [studentGoals, setStudentGoals] = useState([]);
  const [selectedGoalIdx, setSelectedGoalIdx] = useState(0);
  // FP3-126 — currentStudentIdRef lets the goalsByStudent effect
  // re-slice the visible goal panel when the batch map refreshes
  // (e.g. after a goal is added mid-session via a supervisor tab).
  const currentStudentIdRef = useRef(null);
  const [trials, setTrials] = useState([]);
  const [sessionNote, setSessionNote] = useState("");
  // FP3-128 — Previous relevant session note surfaced read-only
  // beneath the (blank, editable) SESSION NOTE textarea so the
  // provider remembers where therapy left off. Session-level:
  // never re-fetched on roster switch; never prefilled into the
  // current-note textarea; canonical previous derived server-side
  // per `_recurring_block_id` / individual student / group-set
  // rules (see routes/sessions.py::get_session_previous_note).
  const [previousNote, setPreviousNote] = useState({
    loading: false,
    session: null,
  });
  const [endingSession, setEndingSession] = useState(false);
  // Iter 33d Follow-up 14c — running-elapsed timer on Live Session.
  // `sessionStartedAt` is captured at the wall-clock moment the user
  // launched the session and re-renders are driven by a 1s `nowTick`
  // interval so the header shows a live MM:SS duration.
  const [sessionStartedAt, setSessionStartedAt] = useState(null);
  const [nowTick, setNowTick] = useState(() => Date.now());
  // Iter 32 — per-student attendance state ({studentId: "present" |
  // "absent" | "unavailable"}). Defaults all roster students to "present"
  // when the session opens. Persists with the session record on End.
  const [attendance, setAttendance] = useState({});
  // Iter 45m — Quick Session service-minute selector. Defaults to 30
  // minutes (industry standard for an unscheduled service). Provider
  // can flip to 10 or 15 in the live header. This is the value
  // counted toward fidelity / service delivery, NOT the live tracker
  // elapsed time.
  const [quickDurationMinutes, setQuickDurationMinutes] = useState(30);

  useEffect(() => {
    loadData();
  }, []);

  // Tick once a second whenever an active session is open so the
  // header duration label updates live.
  useEffect(() => {
    if (!activeSession) return;
    const id = setInterval(() => setNowTick(Date.now()), 1000);
    return () => clearInterval(id);
  }, [activeSession]);

  // Auto-launch grouped Live Session when navigated from Today/Schedule with ?launch=ID1,ID2
  // Uses backend /api/sessions/launch which resolves synthetic 'rb__' IDs into real rows.
  useEffect(() => {
    if (!launchParam || activeSession) return;
    const ids = launchParam.split(",").filter(Boolean);
    if (ids.length === 0) return;
    (async () => {
      try {
        const res = await api.launchSessions({ session_ids: ids });
        const sessions = res.data?.sessions || [];
        if (sessions.length === 0) {
          toast.error("Could not load that scheduled session");
          return;
        }
        // Merge students preserving order, dedup by id
        const merged = [];
        const seen = new Set();
        for (const s of sessions) {
          for (const st of s.students || []) {
            if (!seen.has(st.id)) {
              seen.add(st.id);
              merged.push(st);
            }
          }
        }
        const virtual = {
          id: sessions[0].id,
          _underlyingIds: sessions.map((s) => s.id),
          _isGrouped: sessions.length > 1 || merged.length > 1,
          students: merged,
          student_ids: merged.map((s) => s.id),
          start_time: sessions[0].start_time,
          end_time: sessions[0].end_time,
          session_type: merged.length > 1 ? "group" : sessions[0].session_type,
          provider_id: sessions[0].provider_id,
          service_type: sessions[0].service_type,
          status: "scheduled",
          date: sessions[0].date,
        };
        startSession(virtual);
      } catch (e) {
        console.error("Launch failed:", e);
        toast.error("Failed to launch session");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [launchParam]);

  const loadData = async () => {
    setLoading(true);
    try {
      // Use the user's local YYYY-MM-DD so the manual-selection list matches the Today page.
      const d = new Date();
      const localToday = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
      const [provRes, dashRes] = await Promise.all([
        api.getCurrentProvider(),
        api.getTodayDashboard({ date: localToday }),
      ]);
      // Iter 45e — scope the manual-selection student list to the
      // current provider so you can't collect data against another
      // provider's caseload.
      const studRes = provRes.data?.id
        ? await api.getStudents({ provider_id: provRes.data.id })
        : { data: [] };
      setProvider(provRes.data);
      // Today endpoint already returns grouped sessions; show them as the manual selection list.
      const open = (dashRes.data?.today_sessions || []).filter(
        (s) => s.status !== "completed" && s.status !== "cancelled"
      );
      setTodaySessions(open);
      setAllStudents(studRes.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const startSession = async (session) => {
    // If the session is a synthetic recurring-block materialization (id starts with "rb__"),
    // create a real session row first so status/trials can persist.
    let realSession = session;
    if (session.id && typeof session.id === "string" && session.id.startsWith("rb__")) {
      try {
        const res = await api.createSession({
          student_ids: session.student_ids || session.students?.map((s) => s.id) || [],
          provider_id: session.provider_id,
          session_type: session.session_type || "individual",
          date: session.date,
          start_time: session.start_time,
          end_time: session.end_time,
          status: "scheduled",
          room_id: session.room_id || "",
          service_type: session.service_type || "",
          is_recurring: true,
        });
        realSession = { ...session, id: res.data.id, _underlyingIds: [res.data.id] };
      } catch (e) {
        console.error("Materialize failed:", e);
      }
    }
    setActiveSession(realSession);
    setSessionStartedAt(Date.now());
    setSessionStudents(realSession.students || []);
    setSelectedStudentIdx(0);
    setTrials([]);
    setSessionNote("");
    // Iter 32 — default every student to "present" at session start
    const initialAttendance = {};
    (realSession.students || []).forEach((st) => {
      initialAttendance[st.id] = "present";
    });
    setAttendance(initialAttendance);
    if (realSession.students?.length > 0) {
      currentStudentIdRef.current = realSession.students[0].id;
      setSelectedGoalIdx(0);
      // FP3-126 — Fetch the session-scoped batch INLINE so the
      // first student's goals appear immediately (no brief
      // "No active goals" flash while the effect races).
      // The `useEffect` on activeSession.id also fires this fetch
      // and setsState — either write wins; both settle to the
      // same value.
      try {
        const res = await api.getSessionGoals(realSession.id);
        const map = res.data?.goals_by_student || {};
        setGoalsByStudent(map);
        const first = realSession.students[0].id;
        if (Array.isArray(map[first])) {
          setStudentGoals(map[first]);
        }
      } catch (e) {
        // Fall back to per-student /api/goals for the current
        // student. Non-fatal: the effect will retry too.
        console.warn("getSessionGoals inline failed; falling back:", e);
        await loadGoalsForStudent(realSession.students[0].id);
      }
    }
    // For grouped sessions, mark all underlying sessions as in-progress
    const ids = realSession._underlyingIds || [realSession.id];
    for (const id of ids) {
      try {
        await api.updateSession(id, { status: "in-progress" });
      } catch (e) {
        // Skip synthetic IDs (e.g., quick sessions) gracefully
      }
    }
  };

  // FP3-126 — When the session is launched (either fresh or from
  // Today/Schedule with ?launch=), fetch active goals for EVERY
  // student in the roster in one round-trip via the session-scoped
  // endpoint. This bypasses the caseload-scoped /api/goals filter
  // that silently returned [] for co-treated students (Ta'Veion
  // reproducer). The `goalsByStudent` map is the single source
  // of truth for the goal panel; `loadGoalsForStudent` now just
  // slices from it.
  const [goalsByStudent, setGoalsByStudent] = useState({});

  useEffect(() => {
    if (!activeSession?.id) {
      setGoalsByStudent({});
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await api.getSessionGoals(activeSession.id);
        if (!cancelled) {
          setGoalsByStudent(res.data?.goals_by_student || {});
        }
      } catch (e) {
        console.error("Failed to load session goals:", e);
        if (!cancelled) setGoalsByStudent({});
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activeSession?.id]);

  // FP3-128 — Fetch the previous relevant session note ONCE per
  // session id. Session-level (never keyed on selectedStudentIdx),
  // so switching between group members does not re-fetch and does
  // not change the surfaced note. Never prefilled into the current
  // Session Note textarea.
  useEffect(() => {
    if (!activeSession?.id) {
      setPreviousNote({ loading: false, session: null });
      return;
    }
    let cancelled = false;
    setPreviousNote({ loading: true, session: null });
    (async () => {
      try {
        const res = await api.getSessionPreviousNote(activeSession.id);
        if (!cancelled) {
          setPreviousNote({
            loading: false,
            session: res.data?.previous_session || null,
          });
        }
      } catch (e) {
        // Non-fatal — the FE renders the empty-state.
        console.warn("Failed to load previous session note:", e);
        if (!cancelled) setPreviousNote({ loading: false, session: null });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activeSession?.id]);

  const loadGoalsForStudent = async (studentId) => {
    // Prefer the session-scoped batch (FP3-126). Fall back to
    // /api/goals only when the batch has no entry for this student
    // (e.g., ad-hoc / Quick Session paths without an activeSession).
    const fromMap = goalsByStudent[studentId];
    if (Array.isArray(fromMap)) {
      setStudentGoals(fromMap);
      setSelectedGoalIdx(0);
      return;
    }
    try {
      const res = await api.getGoals({ student_id: studentId, status: "active" });
      setStudentGoals(res.data);
      setSelectedGoalIdx(0);
    } catch (e) {
      console.error(e);
      setStudentGoals([]);
    }
  };

  // FP3-126 — Keep the visible goal panel in sync with the
  // session-scoped map: whenever `goalsByStudent` refreshes AND a
  // student is already selected, re-slice.
  useEffect(() => {
    if (!currentStudentIdRef.current) return;
    const fresh = goalsByStudent[currentStudentIdRef.current];
    if (Array.isArray(fresh)) {
      setStudentGoals(fresh);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [goalsByStudent]);

  const selectStudent = async (idx) => {
    setSelectedStudentIdx(idx);
    if (sessionStudents[idx]) {
      currentStudentIdRef.current = sessionStudents[idx].id;
      await loadGoalsForStudent(sessionStudents[idx].id);
    }
  };

  const currentStudent = sessionStudents[selectedStudentIdx];
  const currentGoal = studentGoals[selectedGoalIdx];

  const currentTrials = trials.filter(
    (t) => t.student_id === currentStudent?.id && t.goal_id === currentGoal?.id
  );

  const correctCount = currentTrials.filter((t) => t.result === "correct").length;
  const accuracy = currentTrials.length > 0 ? Math.round((correctCount / currentTrials.length) * 100) : 0;

  const recordTrial = useCallback((result) => {
    if (!currentStudent || !currentGoal) return;
    const trial = {
      student_id: currentStudent.id,
      goal_id: currentGoal.id,
      trial_num: currentTrials.length + 1,
      result,
      timestamp: new Date().toISOString(),
    };
    setTrials((prev) => [...prev, trial]);
  }, [currentStudent, currentGoal, currentTrials.length]);

  const undoLastTrial = () => {
    if (currentTrials.length === 0) return;
    setTrials((prev) => {
      const last = [...prev];
      const idx = last.findLastIndex(
        (t) => t.student_id === currentStudent?.id && t.goal_id === currentGoal?.id
      );
      if (idx >= 0) last.splice(idx, 1);
      return last;
    });
  };

  const endSession = async () => {
    if (!activeSession || !provider) return;
    setEndingSession(true);
    try {
      // Iter 33d Follow-up 12 — Critical fix: mark the underlying
      // session(s) completed BEFORE attempting trial-batch and note
      // saves. Previously, if the trial batch save failed for any
      // reason (bad goal id, network blip, etc.), the entire try block
      // jumped to the catch and the session was left stuck at
      // status="in-progress" — exactly the bug the user reported on
      // Apr 28 sessions. Completing first guarantees Today / Session
      // History / Tracker reflect the correct status even on partial
      // save failure.
      const studentStatuses = sessionStudents.map(
        (st) => attendance[st.id] || "present"
      );
      const anyPresent = studentStatuses.some((s) => s === "present");
      const allUnavailable =
        studentStatuses.length > 0 && studentStatuses.every((s) => s === "unavailable");
      const allAbsent =
        studentStatuses.length > 0 && studentStatuses.every((s) => s === "absent");
      const sessionAttendance = anyPresent
        ? "present"
        : allUnavailable
        ? "unavailable"
        : allAbsent
        ? "absent"
        : "absent";
      const completionIds = activeSession._underlyingIds || [activeSession.id];
      let failedCompletions = 0;
      // Iter 33d Follow-up 14b — Quick Session save fix.
      // Quick sessions hold a synthetic id ("quick-<ts>") that was never
      // persisted. The previous code path called `updateSession` on that
      // synthetic id, which 404'd → "Session save partially failed
      // (1 of 1)". Quick Sessions need to CREATE a real row instead.
      // We create one row per selected student so individual / group
      // semantics line up with regular launched sessions, and so
      // Session History on each student page resolves correctly.
      let realQuickSessionIds = [];
      if (activeSession._isQuick) {
        const today = new Date();
        const dateStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
        const startStr = activeSession.start_time || today.toTimeString().slice(0, 5);
        // Iter 45m — Quick Session service minutes are driven by the
        // provider's 10/15/30 selector, NOT the live tracker elapsed
        // time. End time = start + chosen minutes so Session History,
        // Calendar, and Tracker all show the intended service window.
        // The actual tracker elapsed (wall-clock) is captured
        // separately as `tracker_elapsed_minutes` for audit only.
        const [sh, sm] = startStr.split(":").map((n) => parseInt(n, 10));
        const startMinutes = sh * 60 + sm;
        const chosenMinutes = quickDurationMinutes;
        const endMinutes = startMinutes + chosenMinutes;
        const endStr = `${String(Math.floor(endMinutes / 60) % 24).padStart(2, "0")}:${String(endMinutes % 60).padStart(2, "0")}`;
        const trackerElapsedMs = sessionStartedAt ? Date.now() - sessionStartedAt : 0;
        const trackerElapsedMin = Math.max(0, Math.round(trackerElapsedMs / 60000));
        const sessionType =
          sessionStudents.length > 1 ? "group" : "individual";
        const allStudentIds = sessionStudents.map((st) => st.id);
        // Pick service_type from the first student's first service area
        const firstSt = sessionStudents[0];
        const svcType = (firstSt?.service_areas || [])[0] || "";
        try {
          const created = await api.createSession({
            student_ids: allStudentIds,
            provider_id: provider.id,
            session_type: sessionType,
            date: dateStr,
            start_time: startStr,
            end_time: endStr,
            status: "scheduled",
            is_recurring: false,
            service_type: svcType,
          });
          const newId = (created?.data || created)?.id;
          if (newId) realQuickSessionIds.push(newId);
        } catch (createErr) {
          console.error("Quick session create failed", createErr);
          failedCompletions += 1;
        }
        // Now flip the freshly-created row to completed with attendance
        // and explicit delivered_minutes from the quick selector.
        const updateResults = await Promise.allSettled(
          realQuickSessionIds.map((id) =>
            api.updateSession(id, {
              status: "completed",
              attendance: sessionAttendance,
              student_attendance: attendance,
              delivered_minutes: chosenMinutes,
              tracker_elapsed_minutes: trackerElapsedMin,
              // Iter 45o — session-level note saved once on the
              // session doc. Empty string is valid (clears any
              // prior note); undefined would be skipped by the
              // pydantic optional model. We always send the
              // current textarea value so what the provider sees
              // matches what gets persisted.
              session_note: sessionNote || "",
            }),
          ),
        );
        failedCompletions += updateResults.filter(
          (r) => r.status === "rejected",
        ).length;
      } else {
        // Iter 45m — Scheduled sessions: do NOT touch start_time /
        // end_time. The originally-scheduled window is the source of
        // truth for service minutes (a 30-min scheduled block always
        // counts 30 minutes regardless of how briefly the live
        // tracker was open). We do persist tracker_elapsed_minutes
        // for audit/display; it does not drive fidelity.
        const trackerElapsedMs = sessionStartedAt ? Date.now() - sessionStartedAt : 0;
        const trackerElapsedMin = Math.max(0, Math.round(trackerElapsedMs / 60000));
        const completionResults = await Promise.allSettled(
          completionIds.map((id) =>
            api.updateSession(id, {
              status: "completed",
              attendance: sessionAttendance,
              student_attendance: attendance,
              tracker_elapsed_minutes: trackerElapsedMin,
              // Iter 45o — session note persisted on the session
              // doc itself (once per session, shared across linked
              // students for group sessions).
              session_note: sessionNote || "",
            }),
          ),
        );
        failedCompletions = completionResults.filter(
          (r) => r.status === "rejected",
        ).length;
        if (failedCompletions > 0) {
          console.error(
            `${failedCompletions} session completion(s) failed`,
            completionResults,
          );
        }
      }
      // Iter 33d Follow-up 14b — when the session was a Quick Session,
      // route trials and notes to the freshly created real session id
      // (not the synthetic "quick-…" id) so they FK correctly into
      // db.sessions and surface in Session History / Tracker.
      const persistSessionId = activeSession._isQuick
        ? realQuickSessionIds[0] || activeSession.id
        : activeSession.id;
      const totalCompletionTargets = activeSession._isQuick
        ? 1
        : completionIds.length;

      // Group trials by student+goal and create trial runs
      const grouped = {};
      trials.forEach((t) => {
        const key = `${t.student_id}_${t.goal_id}`;
        if (!grouped[key]) grouped[key] = { student_id: t.student_id, goal_id: t.goal_id, responses: [] };
        grouped[key].responses.push({ trial_num: t.trial_num, result: t.result });
      });

      // Iter 45n — Trial-run date MUST be the session's local service
      // date, not `new Date().toISOString()`. Saving in the evening
      // local time (e.g. May 7 6:13 PM PT) used to produce a UTC date
      // of May 8 → Sessions tab said May 7 but Data tab said May 8.
      // The session row's `date` field is already local-correct
      // (built from getFullYear/getMonth/getDate at quick-create or
      // from the scheduled block's date for materialised sessions),
      // so we trust it as the single source of truth. Final fallback
      // uses local Y/M/D, never `toISOString`.
      const localTodayYmd = (() => {
        const d = new Date();
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
      })();
      const trialDate = activeSession.date || localTodayYmd;

      const batchData = Object.values(grouped).map((g) => ({
        session_id: persistSessionId,
        student_id: g.student_id,
        goal_id: g.goal_id,
        provider_id: provider.id,
        responses: g.responses,
        date: trialDate,
      }));

      if (batchData.length > 0) {
        try {
          await api.createTrialRunsBatch(batchData);
        } catch (trialErr) {
          console.error("Trial batch save failed", trialErr);
          toast.error(
            `Session marked completed, but trial data save failed: ${trialErr.message || ""}`
          );
        }
      }

      // Iter 45o — Session note now lives on the session doc itself
      // (sent above as `session_note` in the updateSession payload).
      // The legacy per-student note creation has been removed; one
      // session = one note, shared across linked students for group
      // sessions, exactly as the product spec calls for.

      if (failedCompletions > 0) {
        toast.error(
          `Session save partially failed (${failedCompletions} of ${totalCompletionTargets}). Please refresh and verify.`
        );
      } else {
        // Iter 33d Follow-up 13 — Add a "View in history" action button
        // so the user can verify their save landed in Session History
        // without having to navigate manually.
        const firstStudent = sessionStudents[0];
        toast.success(`Session completed. ${trials.length} trials recorded.`, {
          action: firstStudent
            ? {
                label: "View in history",
                onClick: () =>
                  navigate(`/students/${firstStudent.id}?tab=sessions`),
              }
            : undefined,
          duration: 6000,
        });
      }
      setActiveSession(null);
      setSessionStartedAt(null);
      setTrials([]);
      setSessionNote("");
      // FP3-125 — bust admin/progress/reports caches so the new
      // evidence lifts stale-goal flags + refreshes Reports the
      // moment the provider returns to those surfaces.
      invalidateAfterMutation.sessionLogged();
      // If launched from Today via ?launch=, return there for clarity
      if (launchParam) navigate("/");
      loadData();
    } catch (e) {
      console.error(e);
      toast.error("Failed to save session data");
    } finally {
      setEndingSession(false);
    }
  };

  const cancelSession = async () => {
    if (!activeSession) return;
    try {
      if (activeSession._isQuick) {
        // Quick sessions just discard
      } else {
        const ids = activeSession._underlyingIds || [activeSession.id];
        for (const id of ids) {
          try {
            await api.updateSession(id, { status: "scheduled" });
          } catch (e) {
            // skip synthetic IDs
          }
        }
      }
      setActiveSession(null);
      setSessionStartedAt(null);
      setTrials([]);
      setSessionNote("");
      setSessionStudents([]);
      if (activeSession._isQuick || launchParam) {
        navigate("/");
      }
      toast.info("Session cancelled");
    } catch (e) {
      console.error(e);
    }
  };

  const startQuickSession = async () => {
    if (quickSelected.length === 0) { toast.error("Select at least one student"); return; }
    const students = allStudents.filter((s) => quickSelected.includes(s.id));
    setActiveSession({ id: "quick-" + Date.now(), session_type: quickSelected.length > 1 ? "group" : "individual", start_time: new Date().toTimeString().slice(0, 5), end_time: "", _isQuick: true });
    setSessionStartedAt(Date.now());
    setSessionStudents(students);
    setSelectedStudentIdx(0);
    setTrials([]);
    setSessionNote("");
    // Iter 32 — default attendance for quick-session students too
    const initial = {};
    students.forEach((st) => { initial[st.id] = "present"; });
    setAttendance(initial);
    if (students.length > 0) {
      currentStudentIdRef.current = students[0].id;
      await loadGoalsForStudent(students[0].id);
    }
  };

  if (loading) {
    return (
      <div className="p-8 max-w-6xl mx-auto space-y-6" data-testid="session-loading">
        <Skeleton className="h-10 w-64" />
        <div className="grid grid-cols-3 gap-4"><Skeleton className="h-32" /><Skeleton className="h-32" /><Skeleton className="h-32" /></div>
      </div>
    );
  }

  // FP3-035F Issue 2 — Live Session is the highest-risk mutation
  // surface (creates session + trials + scores under the shadowed
  // provider). Hard-block the entire page in read-only mode with a
  // calm explanation + a back-link to the provider's Today.
  if (readOnly.active) {
    return (
      <div
        className="p-8 max-w-3xl mx-auto animate-fade-in"
        data-testid="live-session-readonly"
      >
        <button
          data-testid="live-session-readonly-back"
          onClick={() => navigate("/")}
          className="flex items-center gap-1.5 text-sm text-[#6B7280] hover:text-[#1F2937] transition-colors mb-4"
        >
          <ArrowLeft size={16} /> Back to Today
        </button>
        <Card className="bg-white rounded-2xl border border-[#E5E3DB] shadow-sm">
          <CardContent className="p-8 text-center space-y-3">
            <Lightning size={36} weight="duotone" className="mx-auto text-[#9C6B2E]" />
            <h2 className="text-lg font-semibold text-[#1F2937] heading-font">
              Live Session is unavailable in read-only mode
            </h2>
            <p className="text-sm text-[#6B7280]">
              {readOnly.mode === "admin_oversight"
                ? "You are viewing this app as an administrator. Live data collection creates new session records, so it is disabled for admin oversight."
                : "You're viewing this provider's workspace as an admin. Live data collection creates new session records, so it is disabled while shadowing. Exit to the Admin View to return to your normal controls."}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Session selection view
  if (!activeSession) {
    // Quick Session mode - student picker
    if (isQuickMode) {
      const toggleQS = (id) => setQuickSelected((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);
      return (
        <div className="p-8 max-w-3xl mx-auto animate-fade-in" data-testid="quick-session-picker">
          <button data-testid="back-from-quick" onClick={() => navigate("/")} className="flex items-center gap-1.5 text-sm text-[#6B7280] hover:text-[#1F2937] transition-colors mb-4">
            <ArrowLeft size={16} /> Back to Today
          </button>
          <h1 className="text-2xl font-semibold text-[#1F2937] heading-font tracking-tight mb-1" data-testid="quick-session-title">Quick Session</h1>
          <p className="text-sm text-[#6B7280] mb-6">Select students for a one-time data collection session.</p>
          <Card className="bg-white rounded-2xl border border-[#E5E3DB] shadow-sm mb-6">
            <CardContent className="p-5">
              <ScrollArea className="h-72">
                <div className="space-y-1">
                  {allStudents.filter((s) => s.assigned_providers?.includes(provider?.id)).map((s) => (
                    <label key={s.id} className="flex items-center gap-3 p-2.5 rounded-lg hover:bg-[#F9F8F6] cursor-pointer transition-colors" data-testid={`qs-pick-${s.id}`}>
                      <Checkbox checked={quickSelected.includes(s.id)} onCheckedChange={() => toggleQS(s.id)} />
                      <div className="flex-1">
                        <span className="text-sm font-medium text-[#1F2937]">{s.first_name} {s.last_name}</span>
                        <span className="text-xs text-[#6B7280] ml-2">Gr. {s.grade}</span>
                      </div>
                      <div className="flex gap-1">{((s.services && s.services.length > 0 ? s.services.map((sv) => sv.label || sv.key) : (s.service_areas || [])).slice(0, 2)).map((a) => (<span key={a} className="text-[10px] px-1.5 py-0.5 rounded-full bg-[#E1EAE4] text-[#1E3F33]">{a}</span>))}</div>
                    </label>
                  ))}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
          <div className="flex items-center justify-between">
            <span className="text-sm text-[#6B7280]">{quickSelected.length} student{quickSelected.length !== 1 ? "s" : ""} selected</span>
            <Button onClick={startQuickSession} disabled={quickSelected.length === 0} className="bg-[#1E3F33] hover:bg-[#173127] text-white rounded-lg text-sm" data-testid="start-quick-session-btn">
              <Lightning size={16} weight="duotone" className="mr-1.5" /> Start Quick Session
            </Button>
          </div>
        </div>
      );
    }

    // Normal session selection
    return (
      <div className="p-8 max-w-5xl mx-auto animate-fade-in" data-testid="session-select-page">
        <h1 className="text-2xl font-semibold text-[#1F2937] heading-font tracking-tight mb-2" data-testid="session-page-title">
          Live Session
        </h1>
        <p className="text-sm text-[#6B7280] mb-6">Select a scheduled session to begin data collection.</p>

        {todaySessions.length === 0 ? (
          <Card className="bg-white rounded-2xl border border-[#E5E3DB] shadow-sm">
            <CardContent className="p-12 text-center">
              <Lightning size={40} weight="duotone" className="text-[#8FA998] mx-auto mb-3" />
              <p className="text-sm text-[#6B7280]">No scheduled sessions for today.</p>
              <p className="text-xs text-[#6B7280] mt-1">Sessions appear here when scheduled on the Today dashboard.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3" data-testid="session-list">
            {todaySessions.map((session) => {
              const launchIds = session.session_ids?.length > 0 ? session.session_ids.join(",") : session.id;
              const studentLabel =
                session.students?.length > 2
                  ? `${session.students[0].first_name} ${session.students[0].last_name?.[0]}. + ${session.students.length - 1} more`
                  : session.students?.map((s) => `${s.first_name} ${s.last_name}`).join(", ");
              return (
                <Card
                  key={session.id}
                  data-testid={`start-session-${session.id}`}
                  className="bg-white rounded-2xl border border-[#E5E3DB] shadow-sm fp-card cursor-pointer overflow-hidden"
                  onClick={() => navigate(`/session?launch=${launchIds}`)}
                >
                  <CardContent className="p-5 flex items-center gap-5">
                    <div className="min-w-[80px] text-right">
                      <p className="text-sm font-semibold text-[#1F2937]">{formatTime(session.start_time)}</p>
                      <p className="text-xs text-[#6B7280]">{formatTime(session.end_time)}</p>
                    </div>
                    <Separator orientation="vertical" className="h-12 bg-[#E5E3DB]" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-[#1F2937]">{studentLabel}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <Badge
                          variant="outline"
                          className={`text-[10px] ${
                            session.is_grouped
                              ? "border-[#1E3F33]/30 bg-[#E8F0EB] text-[#1E3F33]"
                              : "border-[#E5E3DB] text-[#6B7280]"
                          }`}
                        >
                          {session.is_grouped ? `group · ${session.students.length}` : session.session_type}
                        </Badge>
                        {session.service_type && (
                          <span className="text-[10px] text-[#8FA998]">{session.service_type}</span>
                        )}
                      </div>
                    </div>
                    <Button className="bg-[#1E3F33] hover:bg-[#173127] text-white rounded-lg text-sm" data-testid={`begin-session-btn-${session.id}`}>
                      <Play size={16} weight="duotone" className="mr-1.5" /> Start
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  // Active data collection view
  return (
    <div className="p-6 max-w-7xl mx-auto animate-fade-in" data-testid="live-data-collection">
      {/* Session Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="flex items-center gap-2">
            <div className="w-2.5 h-2.5 rounded-full bg-[#1E3F33] animate-pulse" />
            <h1 className="text-lg font-semibold text-[#1F2937] heading-font">Live Session</h1>
            <Badge className="bg-[#E8F0EB] text-[#1E3F33] text-[10px] hover:bg-[#E8F0EB]">{activeSession.session_type}</Badge>
          </div>
          <p className="text-xs text-[#6B7280] mt-0.5" data-testid="live-session-elapsed">
            {(() => {
              const elapsedMs = sessionStartedAt ? Math.max(0, nowTick - sessionStartedAt) : 0;
              const totalSec = Math.floor(elapsedMs / 1000);
              const mm = Math.floor(totalSec / 60);
              const ss = totalSec % 60;
              const elapsed = `${String(mm).padStart(2, "0")}:${String(ss).padStart(2, "0")}`;
              return `${formatTime(activeSession.start_time)} \u00b7 ${elapsed} elapsed \u00b7 ${trials.length} trials recorded`;
            })()}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* Iter 45m — Quick Session service-minute selector. Only
              shown for quick (unscheduled) sessions; scheduled
              sessions inherit their duration from the scheduled
              block. Default 30 min; 10 / 15 also selectable. This is
              the value counted toward fidelity, not the live tracker
              elapsed time. */}
          {activeSession._isQuick && (
            <div
              data-testid="quick-session-duration-selector"
              className="flex items-center gap-1 flex-wrap bg-[#F9F8F6] border border-[#E5E3DB] rounded-lg p-0.5"
            >
              <span className="text-[10px] text-[#6B7280] uppercase tracking-wider px-2 font-medium">
                Service min
              </span>
              {/* FP3-034H — Shared with Document Session so live-tracker
                  quick sessions can record the same common cycles
                  (20 / 25 / 35 / 40 / 45 min) without drift. */}
              {DOCUMENT_SESSION_MINUTE_OPTIONS.map((m) => (
                <button
                  key={m}
                  type="button"
                  data-testid={`quick-duration-${m}`}
                  onClick={() => setQuickDurationMinutes(m)}
                  className={`text-xs font-semibold px-2 py-1 rounded transition-colors ${
                    quickDurationMinutes === m
                      ? "bg-[#1E3F33] text-white"
                      : "text-[#6B7280] hover:bg-white"
                  }`}
                >
                  {m}
                </button>
              ))}
            </div>
          )}
          <Button
            data-testid="cancel-session-btn"
            onClick={cancelSession}
            variant="outline"
            className="border-[#E5E3DB] text-[#6B7280] hover:bg-[#F9F8F6] rounded-lg text-sm"
          >
            <ArrowLeft size={16} className="mr-1.5" />
            Cancel
          </Button>
          <Button
            data-testid="end-session-btn"
            onClick={endSession}
            disabled={endingSession}
            className="bg-[#B85C5C] hover:bg-[#a04e4e] text-white rounded-lg text-sm"
          >
            <Stop size={16} weight="duotone" className="mr-1.5" />
            {endingSession ? "Saving..." : "Save / End Session"}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Student Roster */}
        <div className="lg:col-span-3">
          <Card className="bg-white rounded-2xl border border-[#E5E3DB] shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold text-[#6B7280] uppercase tracking-wider heading-font">Students</CardTitle>
            </CardHeader>
            <CardContent className="space-y-1">
              {sessionStudents.map((student, idx) => {
                const studentTrialCount = trials.filter((t) => t.student_id === student.id).length;
                const att = attendance[student.id] || "present";
                const isAbsent = att === "absent" || att === "unavailable";
                return (
                  <div
                    key={student.id}
                    data-testid={`roster-student-wrap-${idx}`}
                    className={`p-3 rounded-xl transition-all ${
                      idx === selectedStudentIdx && !isAbsent
                        ? "bg-[#E8F0EB] border border-[#8FA998]/30"
                        : "hover:bg-[#F9F8F6]"
                    } ${isAbsent ? "opacity-60" : ""}`}
                  >
                    <button
                      data-testid={`roster-student-${idx}`}
                      onClick={() => !isAbsent && selectStudent(idx)}
                      disabled={isAbsent}
                      className="w-full flex items-center gap-3 text-left"
                    >
                      <div className="w-8 h-8 rounded-full bg-[#F3F1EC] flex items-center justify-center text-xs font-semibold text-[#1E3F33] heading-font">
                        {student.first_name?.[0]}{student.last_name?.[0]}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-[#1F2937] truncate">{student.first_name}</p>
                        <p className="text-[11px] text-[#6B7280]">Gr. {student.grade}</p>
                      </div>
                      {studentTrialCount > 0 && (
                        <span className="text-[10px] font-semibold text-[#1E3F33] bg-[#E8F0EB] px-1.5 py-0.5 rounded">{studentTrialCount}</span>
                      )}
                    </button>
                    {/* Iter 32 — Attendance toggle. Default present; absent
                        and unavailable disable trial recording for this
                        student. State persists with the session record. */}
                    <div
                      data-testid={`attendance-toggle-${student.id}`}
                      className="flex gap-1 mt-2"
                    >
                      {["present", "absent", "unavailable"].map((opt) => {
                        const active = att === opt;
                        const palette = {
                          present: active ? "bg-[#3F7A5C] text-white" : "bg-[#EFF3F1] text-[#3F7A5C] hover:bg-[#dbe6df]",
                          absent: active ? "bg-[#B85C5C] text-white" : "bg-[#FBEFEC] text-[#B85C5C] hover:bg-[#f5dbd6]",
                          unavailable: active ? "bg-[#9C6B2E] text-white" : "bg-[#FDF8F3] text-[#9C6B2E] hover:bg-[#f5e9d6]",
                        }[opt];
                        return (
                          <button
                            key={opt}
                            type="button"
                            data-testid={`attendance-${opt}-${student.id}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              setAttendance((prev) => ({ ...prev, [student.id]: opt }));
                            }}
                            className={`flex-1 text-[10px] font-semibold uppercase tracking-wider py-1 rounded-md transition-colors ${palette}`}
                          >
                            {opt === "unavailable" ? "Unavail." : opt}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </CardContent>
          </Card>
        </div>

        {/* Trial Recording */}
        <div className="lg:col-span-6">
          {/* Goal Selector */}
          {studentGoals.length > 0 && (
            <div className="flex gap-2 mb-4 flex-wrap" data-testid="goal-selector">
              {studentGoals.map((goal, idx) => (
                <button
                  key={goal.id}
                  data-testid={`select-goal-${idx}`}
                  onClick={() => setSelectedGoalIdx(idx)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                    idx === selectedGoalIdx
                      ? "bg-[#1E3F33] text-white"
                      : "bg-[#E1EAE4] text-[#1E3F33] hover:bg-[#d5e5d9]"
                  }`}
                >
                  {goal.category}
                </button>
              ))}
            </div>
          )}

          {currentGoal ? (
            <Card className="bg-white rounded-2xl border border-[#E5E3DB] shadow-sm">
              <CardContent className="p-6">
                {/* Goal Info */}
                <div className="mb-5">
                  <div className="flex items-start gap-2 mb-2">
                    <Target size={16} weight="duotone" className="text-[#1E3F33] mt-0.5 shrink-0" />
                    <p className="text-sm text-[#1F2937] leading-relaxed">{currentGoal.goal_text}</p>
                  </div>
                  <div className="flex items-center gap-3 text-xs text-[#6B7280]">
                    <span>Target: {currentGoal.target_mastery}%</span>
                    <span>Current: {currentGoal.current_mastery}%</span>
                  </div>
                </div>

                <Separator className="bg-[#E5E3DB] mb-5" />

                {/* Trial Buttons */}
                <div className="grid grid-cols-4 gap-3 mb-5" data-testid="trial-buttons">
                  <button
                    data-testid="trial-correct"
                    onClick={() => recordTrial("correct")}
                    className="flex flex-col items-center gap-1.5 p-4 rounded-2xl bg-[#E8F0EB] hover:bg-[#d5e5d9] active:scale-95 transition-all border-2 border-transparent hover:border-[#1E3F33]/20"
                  >
                    <Check size={28} weight="bold" className="text-[#1E3F33]" />
                    <span className="text-xs font-semibold text-[#1E3F33]">Correct</span>
                  </button>
                  <button
                    data-testid="trial-incorrect"
                    onClick={() => recordTrial("incorrect")}
                    className="flex flex-col items-center gap-1.5 p-4 rounded-2xl bg-[#FDF2F2] hover:bg-[#fbe5e5] active:scale-95 transition-all border-2 border-transparent hover:border-[#B85C5C]/20"
                  >
                    <X size={28} weight="bold" className="text-[#B85C5C]" />
                    <span className="text-xs font-semibold text-[#B85C5C]">Incorrect</span>
                  </button>
                  <button
                    data-testid="trial-prompted"
                    onClick={() => recordTrial("prompted")}
                    className="flex flex-col items-center gap-1.5 p-4 rounded-2xl bg-[#EDF2F7] hover:bg-[#dde8f3] active:scale-95 transition-all border-2 border-transparent hover:border-[#4A6B8C]/20"
                  >
                    <Hand size={28} weight="duotone" className="text-[#4A6B8C]" />
                    <span className="text-xs font-semibold text-[#4A6B8C]">Prompted</span>
                  </button>
                  <button
                    data-testid="trial-approximate"
                    onClick={() => recordTrial("approximate")}
                    className="flex flex-col items-center gap-1.5 p-4 rounded-2xl bg-[#FDF8F3] hover:bg-[#f8eee2] active:scale-95 transition-all border-2 border-transparent hover:border-[#D9A05B]/20"
                  >
                    <Tilde size={28} weight="bold" className="text-[#D9A05B]" />
                    <span className="text-xs font-semibold text-[#D9A05B]">Approx.</span>
                  </button>
                </div>

                {/* Trial History */}
                {currentTrials.length > 0 && (
                  <div className="mb-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-semibold text-[#6B7280] uppercase tracking-wider">Trials</span>
                      <button
                        data-testid="undo-trial-btn"
                        onClick={undoLastTrial}
                        className="text-xs text-[#6B7280] hover:text-[#1F2937] transition-colors"
                      >
                        Undo last
                      </button>
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {currentTrials.map((t, i) => (
                        <div
                          key={i}
                          className={`w-8 h-8 rounded-lg text-xs font-bold flex items-center justify-center ${
                            t.result === "correct" ? "bg-[#E8F0EB] text-[#1E3F33]"
                            : t.result === "incorrect" ? "bg-[#FDF2F2] text-[#B85C5C]"
                            : t.result === "prompted" ? "bg-[#EDF2F7] text-[#4A6B8C]"
                            : "bg-[#FDF8F3] text-[#D9A05B]"
                          }`}
                        >
                          {t.result === "correct" ? "+" : t.result === "incorrect" ? "-" : t.result === "prompted" ? "P" : "~"}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Accuracy */}
                {currentTrials.length > 0 && (
                  <div className="flex items-center gap-4" data-testid="trial-accuracy">
                    <div className="flex-1">
                      <Progress value={accuracy} className="h-2 bg-[#E5E3DB]" />
                    </div>
                    <span className="text-lg font-semibold text-[#1E3F33] heading-font min-w-[50px] text-right">{accuracy}%</span>
                    <span className="text-xs text-[#6B7280]">{correctCount}/{currentTrials.length}</span>
                  </div>
                )}
              </CardContent>
            </Card>
          ) : (
            <Card className="bg-white rounded-2xl border border-[#E5E3DB] shadow-sm">
              <CardContent className="p-8 text-center text-[#6B7280] text-sm">
                {studentGoals.length === 0 ? "No active goals for this student." : "Select a goal to begin recording."}
              </CardContent>
            </Card>
          )}
        </div>

        {/* Session Note */}
        <div className="lg:col-span-3">
          <Card className="bg-white rounded-2xl border border-[#E5E3DB] shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold text-[#6B7280] uppercase tracking-wider heading-font flex items-center gap-1.5">
                <Notepad size={14} weight="duotone" /> Session Note
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Textarea
                data-testid="session-note-input"
                value={sessionNote}
                onChange={(e) => setSessionNote(e.target.value)}
                placeholder="What happened in this session — focus, behaviors, what worked, what to try next time..."
                className="border-[#E5E3DB] focus:ring-[#8FA998]/50 focus:border-[#8FA998] min-h-[220px] text-sm resize-y"
              />
              <p className="text-[11px] text-[#9B9890] mt-1.5 italic">
                Saved with the completed session. Visible from this student&apos;s Sessions and Data tabs.
              </p>

              {/* FP3-128 — Read-only Previous Session Note.
                  Session-level context (never re-fetches on roster
                  switch). Never prefills the textarea above. */}
              <div
                className="mt-4 pt-3 border-t border-[#E5E3DB]"
                data-testid="previous-session-note-block"
              >
                <div className="text-[10px] font-semibold text-[#6B7280] uppercase tracking-wider mb-1.5">
                  Previous Session Note
                </div>
                {previousNote.loading ? (
                  <p
                    className="text-[11px] italic text-[#9B9890]"
                    data-testid="previous-session-note-loading"
                  >
                    Loading…
                  </p>
                ) : previousNote.session ? (
                  <div data-testid="previous-session-note">
                    <div
                      className="text-[11px] text-[#6B7280] mb-1"
                      data-testid="previous-session-note-date"
                    >
                      {formatPrevNoteDate(previousNote.session.date)}
                    </div>
                    <div
                      className="text-[12px] text-[#374151] whitespace-pre-wrap leading-relaxed"
                      data-testid="previous-session-note-text"
                    >
                      {previousNote.session.note}
                    </div>
                  </div>
                ) : (
                  <p
                    className="text-[11px] italic text-[#9B9890]"
                    data-testid="previous-session-note-empty"
                  >
                    No previous session note recorded.
                  </p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Session Summary */}
          <Card className="bg-white rounded-2xl border border-[#E5E3DB] shadow-sm mt-4">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold text-[#6B7280] uppercase tracking-wider heading-font">Summary</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-[#6B7280]">Total Trials</span>
                <span className="font-medium text-[#1F2937]" data-testid="total-trials-count">{trials.length}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[#6B7280]">Students</span>
                <span className="font-medium text-[#1F2937]">{sessionStudents.length}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[#6B7280]">Goals Covered</span>
                <span className="font-medium text-[#1F2937]">
                  {new Set(trials.map((t) => t.goal_id)).size}
                </span>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
